import financialApi from 'api/requests/financial';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import { useWithdraw } from 'context/financial/Withdraw';
import currency from 'helpers/currency';
import React, { useState, useMemo, useEffect } from 'react'
import { AlertCircle, ArrowDownCircle, DollarSign, Info, Landmark } from 'lucide-react';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const WITHDRAW_FEE = 2.99;

let convertWithValue = null

/**
 * Mensagem de erro do backend. Saldo insuficiente, conta não aprovada e valor
 * abaixo do mínimo voltam como 422 com texto pronto para o usuário — mostrar
 * "ocorreu um erro" em cima disso esconde justamente o que ele precisa saber.
 */
function extractApiError(error) {
    const response = error?.response?.data;

    return response?.errors?.value?.[0]
        || response?.message
        || 'Ocorreu um erro ao fazer o saque, tente novamente.';
}

export default function WithdrawModal({ visible, onClose, onSucess }) {
    const { withdraw, setWithdraw } = useWithdraw();

    const [withdrawValue, setWithdrawValue] = useState();
    const [invalidField, setInvalidField] = useState(false);
    const [loading, setLoading] = useState(false);
    const [destination, setDestination] = useState(null);

    function parseValue(raw) {
        if (!raw) return 0;
        return Number(raw.replace(',', '.'));
    }

    const parsedValue = useMemo(() => parseValue(withdrawValue), [withdrawValue]);
    const netValue = useMemo(() => Math.max(parsedValue - WITHDRAW_FEE, 0), [parsedValue]);
    const showBreakdown = parsedValue > 0 && !isNaN(parsedValue);

    const MIN_NET_VALUE = 5;
    const MIN_WITHDRAW = MIN_NET_VALUE + WITHDRAW_FEE;

    useEffect(() => {
        if (!visible) {
            return;
        }

        async function loadDestination() {
            try {
                const [{ data: account }, { data: banks }] = await Promise.all([
                    financialApi.getBankAccount(),
                    financialApi.getBanks(),
                ]);

                const bankAccount = account?.data;

                if (!bankAccount) {
                    return;
                }

                const bank = banks?.data?.find(item => item.id === bankAccount.bank_id);

                setDestination({
                    holder: bankAccount.holder,
                    agency: bankAccount.agency,
                    account: bankAccount.account,
                    bankName: bank?.name || 'Conta cadastrada',
                });
            } catch (error) {
                /* Sem os dados o bloco de destino some; o saque em si segue
                   possível e o backend valida a conta de qualquer forma. */
                setDestination(null);
            }
        }

        loadDestination();
    }, [visible]);

    function maskAccount(account) {
        if (!account || account.length <= 4) {
            return account || '';
        }

        return `••••${account.slice(-4)}`;
    }

    function validateOnChange(raw) {
        setInvalidField(false);

        if (!raw) return;

        const num = parseValue(raw);
        if (isNaN(num) || num <= 0) return;

        if (num > withdraw?.availableValue) {
            setInvalidField(`O valor disponível para saque é ${currency.format(withdraw.availableValue)}`);
        } else if (num < MIN_WITHDRAW) {
            setInvalidField(`O valor mínimo de saque é ${currency.format(MIN_WITHDRAW)} (${currency.format(MIN_NET_VALUE)} + taxa de ${currency.format(WITHDRAW_FEE)})`);
        }
    }

    function isValidValue() {
        setInvalidField(false);

        if (!withdrawValue) {
            setInvalidField('Preencha o campo para efetuar o saque!')
            return;
        }

        convertWithValue = parseValue(withdrawValue);
        if (convertWithValue < 0 || isNaN(convertWithValue)) {
            setInvalidField('Insira um valor valido')
            return false;
        }

        if (convertWithValue > withdraw.availableValue) {
            setInvalidField(`O valor disponível para saque é ${currency.format(withdraw.availableValue)}`)
            return false;
        }

        if (convertWithValue < MIN_WITHDRAW) {
            setInvalidField(`O valor mínimo de saque é ${currency.format(MIN_WITHDRAW)} (${currency.format(MIN_NET_VALUE)} + taxa de ${currency.format(WITHDRAW_FEE)})`)
            return false;
        }

        return true;
    }

    async function onWithdraw() {
        if (!isValidValue()) {
            return
        }

        setLoading(true);

        try {
            await financialApi.withdraw({ value: convertWithValue });
            setWithdraw({ ...withdraw, success: Math.random(Infinity) });
            onSucess()
            onClose();
        } catch (error) {
            setInvalidField(extractApiError(error))
        } finally {
            setLoading(false);
        }
    }

    const canWithdraw = withdraw?.availableValue > 0 && withdrawValue && parsedValue >= MIN_WITHDRAW;

    return (
        <Modal isOpen={visible} onClose={() => onClose(false)} size="medium">
            <Wrapper>
                <Header>
                    <IconCircle>
                        <ArrowDownCircle size={22} color={panelAccent(colors.occasionalPurple)} />
                    </IconCircle>
                    <Title>Efetuar saque</Title>
                </Header>

                <BalanceCard>
                    <BalanceLabel>
                        <DollarSign size={14} />
                        Saldo disponível
                    </BalanceLabel>
                    <BalanceValue>
                        {currency.format(withdraw?.availableValue)}
                    </BalanceValue>
                </BalanceCard>

                <InputSection>
                    <InputLabel>Valor do saque</InputLabel>
                    <InputWrapper hasError={!!invalidField}>
                        <CurrencyInputField
                            placeholder='R$ 0,00'
                            layout='straight'
                            onChange={e => {
                                setWithdrawValue(e);
                                validateOnChange(e);
                            }}
                        />
                    </InputWrapper>

                    {invalidField && !loading && (
                        <ErrorMessage>
                            <AlertCircle size={14} />
                            {invalidField}
                        </ErrorMessage>
                    )}

                    <HintRow>
                        <Info size={13} color={colors.night + '88'} />
                        Valor mínimo de saque: {currency.format(MIN_WITHDRAW)} (já inclusa a taxa de {currency.format(WITHDRAW_FEE)})
                    </HintRow>
                </InputSection>

                {showBreakdown && (
                    <FeeBreakdown>
                        <FeeRow>
                            <FeeLabel>Valor solicitado</FeeLabel>
                            <FeeValue>{currency.format(parsedValue)}</FeeValue>
                        </FeeRow>
                        <FeeRow>
                            <FeeLabel>Taxa de transferência</FeeLabel>
                            <FeeValue $negative>- {currency.format(WITHDRAW_FEE)}</FeeValue>
                        </FeeRow>
                        <FeeDivider />
                        <FeeRow>
                            <FeeTotalLabel>Você receberá</FeeTotalLabel>
                            <FeeTotalValue $valid={netValue > 0}>
                                {currency.format(netValue)}
                            </FeeTotalValue>
                        </FeeRow>
                        <FeeExplanation>
                            <Info size={13} />
                            <span>
                                A taxa fixa de {currency.format(WITHDRAW_FEE)} é cobrada pela operadora financeira
                                para processar a transferência com segurança até sua conta.
                                Ela é aplicada uma vez por solicitação de saque, independentemente do valor.
                            </span>
                        </FeeExplanation>
                    </FeeBreakdown>
                )}

                {destination && (
                    <DestinationCard>
                        <DestinationIcon>
                            <Landmark size={16} color={colors.night} />
                        </DestinationIcon>
                        <DestinationInfo>
                            <DestinationLabel>O dinheiro vai para</DestinationLabel>
                            <DestinationBank>{destination.bankName}</DestinationBank>
                            <DestinationDetails>
                                Ag. {destination.agency} · Conta {maskAccount(destination.account)}
                            </DestinationDetails>
                            <DestinationHolder>{destination.holder}</DestinationHolder>
                        </DestinationInfo>
                    </DestinationCard>
                )}

                {loading ? (
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                ) : (
                    <Footer>
                        <CancelButton onClick={() => onClose(false)}>
                            Cancelar
                        </CancelButton>
                        <ConfirmButton
                            disabled={!canWithdraw}
                            onClick={onWithdraw}
                        >
                            <ArrowDownCircle size={18} />
                            Efetuar saque
                        </ConfirmButton>
                    </Footer>
                )}
            </Wrapper>
        </Modal>
    )
}

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const DestinationCard = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 16px;
    padding: 14px 16px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    background: ${colors.neutral};
`;

const DestinationIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;

/* O Modal centraliza o texto por herança, e sem flex:1 o bloco encolhe até a
   maior linha — o resultado era cada linha centrada numa coluna estreita. */
const DestinationInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    flex: 1;
    min-width: 0;
    text-align: left;
`;

const DestinationLabel = styled.span`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: ${colors.nightLight};
`;

const DestinationBank = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DestinationDetails = styled.span`
    font-size: 12px;
    color: ${colors.night};
`;

const DestinationHolder = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
`;

const IconCircle = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: ${panelAccentAlpha(colors.occasionalPurple, '11')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const Title = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const BalanceCard = styled.div`
    background: linear-gradient(135deg, ${panelAccentAlpha(colors.occasionalPurple, '0D')}, ${panelAccentAlpha(colors.occasionalPurple, '05')});
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '22')};
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 24px;
`;

const BalanceLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    margin-bottom: 6px;
`;

const BalanceValue = styled.div`
    font-size: 28px;
    font-weight: 600;
    color: ${panelAccent(colors.occasionalPurple)};
    letter-spacing: -0.5px;
`;

const InputSection = styled.div`
    margin-bottom: 24px;
`;

const InputLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
    display: block;
`;

const InputWrapper = styled.div`
    /* Override the CurrencyInputField straight style */
    input {
        width: 100%;
        background: ${colors.night}08 !important;
        border: 1.5px solid ${({ hasError }) => hasError ? colors.danger : colors.night + '22'} !important;
        border-bottom: 1.5px solid ${({ hasError }) => hasError ? colors.danger : colors.night + '22'} !important;
        border-radius: 10px !important;
        padding: 14px 16px !important;
        font-size: 18px !important;
        font-weight: 600 !important;
        color: ${colors.nightDark} !important;
        transition: all 0.15s ease !important;

        &::placeholder {
            color: ${colors.night}55 !important;
            font-weight: 400 !important;
        }

        &:focus {
            border-color: ${({ hasError }) => hasError ? colors.danger : panelAccent(colors.occasionalPurple)} !important;
            box-shadow: 0 0 0 3px ${({ hasError }) => hasError ? colors.danger + '18' : panelAccentAlpha(colors.occasionalPurple, '18')} !important;
            background: #fff !important;
        }
    }

    /* Hide the default label inside CurrencyInputField */
    label {
        display: none;
    }
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 8px;
    padding: 6px 10px;
    background: ${colors.danger}0A;
    border-radius: 8px;
`;

const HintRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${colors.night}88;
    margin-top: 10px;
`;

const FeeBreakdown = styled.div`
    background: ${colors.night}06;
    border: 1px solid ${colors.night}14;
    border-radius: 12px;
    padding: 16px 18px;
    margin-bottom: 24px;
`;

const FeeRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
`;

const FeeLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.night};
`;

const FeeValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ $negative }) => $negative ? colors.danger : colors.nightDark};
`;

const FeeDivider = styled.div`
    height: 1px;
    background: ${colors.night}18;
    margin: 10px 0;
`;

const FeeTotalLabel = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FeeTotalValue = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${({ $valid }) => $valid ? panelAccent(colors.occasionalPurple) : colors.night + '55'};
`;

const FeeExplanation = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 14px;
    padding: 10px 12px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '08')};
    border-radius: 8px;
    border-left: 3px solid ${panelAccentAlpha(colors.occasionalPurple, '44')};

    svg {
        flex-shrink: 0;
        color: ${panelAccent(colors.occasionalPurple)};
        margin-top: 1px;
    }

    span {
        font-size: 12px;
        line-height: 1.5;
        color: ${colors.night};
    }
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px 0;
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 18px;
    border-top: 1px solid ${colors.night}11;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: flex-end;
    }
`;

const CancelButton = styled.button`
    padding: 10px 20px;
    border: 1px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    order: 2;

    @media screen and (min-width: ${breakpoints.md}) {
        order: 1;
    }

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const ConfirmButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '18' : panelAccent(colors.occasionalPurple)};
    color: ${({ disabled }) => disabled ? colors.night + '66' : '#fff'};
    font-size: 14px;
    font-weight: 600;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;
    order: 1;

    @media screen and (min-width: ${breakpoints.md}) {
        order: 2;
    }

    &:hover:not(:disabled) {
        background: ${panelAccent(colors.secondaryPurple)};
    }
`;
